<template>
  <ShopList
    v-if="!shopsAreLoading"
    :shops="shopsToDisplay"
    :active-shop="activeShop"
    @add-shop="showModal"
    @edit-shop="showModal"
    @select-shop="selectShop"
    @update-shop="handleUpdateShop"
  ></ShopList>
  <div v-if="shopsAreLoading" :class="$style.row">
    <Skeleton width="10rem" height="3rem"></Skeleton>
    <Skeleton width="10rem" height="3rem"></Skeleton>
  </div>
  <Dialog
    v-model:visible="displayModal"
    :header="editingShop ? 'Edit Shop' : 'Add Shop'"
    modal
    :style="{ width: '400px' }"
    dismissable-mask
    @after-hide="stopEditing"
  >
    <ShopForm :shop="editingShop" @save-shop="handleSaveShop" @close="closeModal" />
  </Dialog>
  <ShopDetail v-if="activeShop" :shop="activeShop" :shops-loading="shopsAreLoading" />
</template>

<script lang="ts" setup>
import Dialog from "primevue/dialog";
import Skeleton from "primevue/skeleton";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import ShopDetail from "./components/ShopDetail.vue";
import ShopForm from "./components/ShopForm.vue";
import ShopList from "./components/ShopList.vue";
import useShopsManager from "./hooks/useShopsManager";
import { CreateShopPayload, Shop } from "./shop.types";

const route = useRoute();
const appId = route.params.gameId as string;
const versionId = route.params.versionId as string;

const {
  shopsToDisplay,
  activeShop,
  addShop,
  updateShop,
  setActiveShop,
  refetchShops,
  queryLoading: shopsAreLoading,
} = useShopsManager(appId, versionId);
const displayModal = ref(false);
const editingShop = ref<Shop | null>(null);

const showModal = (shop: Shop) => {
  if (shop) {
    editingShop.value = shop;
  }

  displayModal.value = true;
};

const closeModal = () => {
  if (editingShop.value) {
    editingShop.value = null;
  }

  displayModal.value = false;
};

const stopEditing = () => {
  editingShop.value = null;
};

const selectShop = (shop: Shop) => {
  setActiveShop(shop);
};

const handleSaveShop = async (shop: CreateShopPayload) => {
  const shopId = editingShop.value?.id;
  if (shopId) {
    const { name } = shop;
    await updateShop(shopId, { name });
  } else {
    await addShop(shop);
  }
  closeModal();
};

onMounted(() => {
  refetchShops();
});

const handleUpdateShop = async (input: { shopId: string; isDefault: boolean }) => {
  const { shopId, isDefault } = input;
  await updateShop(shopId, { isDefault });
};
</script>

<style module>
.title {
  margin-bottom: 1rem;
}
.row {
  display: flex;
  gap: 1rem;
  justify-content: start;
  margin-top: 1rem;
}
</style>
